<template>
  <div class="bg-white rounded-xl shadow-sm p-6 sticky top-6">
    <h2 class="text-xl font-bold text-gray-900 mb-6">Filters</h2>
    
    <!-- Search Input -->
    <div class="mb-6">
      <label class="block text-sm font-semibold text-gray-700 mb-2">Zoeken</label>
      <input
        :value="filters.searchQuery"
        @input="updateFilter('searchQuery', $event.target.value)"
        type="text"
        placeholder="Zoek naar bestemming..."
        class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-theme focus:border-transparent"
      />
    </div>

    <!-- Geographic Filters -->
    <div class="mb-6" v-if="regions.length > 0">
      <h3 class="text-sm font-semibold text-gray-900 mb-3">Regio</h3>
      <select
        :value="filters.selectedRegion"
        @change="onRegionChange($event.target.value)"
        class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-theme"
      >
        <option value="">Alle regio's</option>
        <option v-for="region in regions" :key="region.value" :value="region.value">
          {{ region.label }}
        </option>
      </select>
    </div>

    <div class="mb-6" v-if="filters.selectedRegion && filteredCountries.length > 0">
      <h3 class="text-sm font-semibold text-gray-900 mb-3">Land</h3>
      <select
        :value="filters.selectedCountry"
        @change="updateFilter('selectedCountry', $event.target.value)"
        class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-theme"
      >
        <option value="">Alle landen</option>
        <option v-for="country in filteredCountries" :key="country.value" :value="country.value">
          {{ country.label }}
        </option>
      </select>
    </div>

    <!-- Travel Style Filters -->
    <div class="mb-6" v-if="travelStyles.length > 0">
      <h3 class="text-sm font-semibold text-gray-900 mb-3">Reistype</h3>
      <div class="space-y-3 overflow-y-auto">
        <label v-for="style in travelStyles" :key="style.value" class="flex items-center cursor-pointer group">
          <input
            :checked="filters.selectedTravelStyles.includes(style.value)"
            @change="toggleArrayFilter('selectedTravelStyles', style.value)"
            type="checkbox"
            class="h-4 w-4 text-theme focus:ring-theme border-gray-300 rounded"
          />
          <span class="ml-3 text-sm text-gray-700 group-hover:text-gray-900">{{ style.label }}</span>
        </label>
      </div>
    </div>



    <!-- Season Filter -->
    <div class="mb-6" v-if="seasons.length > 0">
      <h3 class="text-sm font-semibold text-gray-900 mb-3">Maanden</h3>
      <div class="space-y-3 overflow-y-auto">
        <label v-for="season in seasons" :key="season.value" class="flex items-center cursor-pointer group">
          <input
            :checked="filters.selectedSeasons.includes(season.value)"
            @change="toggleArrayFilter('selectedSeasons', season.value)"
            type="checkbox"
            class="h-4 w-4 text-theme focus:ring-theme border-gray-300 rounded"
          />
          <span class="ml-3 text-sm text-gray-700 group-hover:text-gray-900">{{ season.label }}</span>
        </label>
      </div>
    </div>

    <!-- Special Interest Filters -->
    <div class="mb-6" v-if="specialInterests.length > 0">
      <h3 class="text-sm font-semibold text-gray-900 mb-3">Speciale categorieën</h3>
      <div class="space-y-3">
        <label v-for="interest in specialInterests" :key="interest.value" class="flex items-center cursor-pointer group">
          <input
            :checked="filters.selectedSpecialInterests.includes(interest.value)"
            @change="toggleArrayFilter('selectedSpecialInterests', interest.value)"
            type="checkbox"
            class="h-4 w-4 text-theme focus:ring-theme border-gray-300 rounded"
          />
          <span class="ml-3 text-sm text-gray-700 group-hover:text-gray-900">{{ interest.label }}</span>
        </label>
      </div>
    </div>

    <!-- Active Filters Display -->
    <div v-if="activeFiltersCount > 0" class="mb-6 p-4 bg-theme-light rounded-lg border border-theme-border">
      <h4 class="text-sm font-semibold text-theme mb-3">Actieve filters ({{ activeFiltersCount }})</h4>
      <div class="flex flex-wrap gap-2">
        <span v-if="filters.searchQuery" class="inline-flex items-center text-xs bg-theme-bg text-white px-3 py-1 rounded-full">
          "{{ filters.searchQuery }}"
          <button @click="updateFilter('searchQuery', '')" class="ml-2 text-white hover:text-theme-hover font-bold">×</button>
        </span>
        <span v-if="filters.selectedRegion" class="inline-flex items-center text-xs bg-theme-bg text-white px-3 py-1 rounded-full">
          {{ getRegionLabel(filters.selectedRegion) }}
          <button @click="updateFilter('selectedRegion', '')" class="ml-2 text-white hover:text-theme-hover font-bold">×</button>
        </span>
        <span v-if="filters.selectedCountry" class="inline-flex items-center text-xs bg-theme-bg text-white px-3 py-1 rounded-full">
          {{ getCountryLabel(filters.selectedCountry) }}
          <button @click="updateFilter('selectedCountry', '')" class="ml-2 text-white hover:text-theme-hover font-bold">×</button>
        </span>
        <span
          v-for="style in filters.selectedTravelStyles"
          :key="style"
          class="inline-flex items-center text-xs bg-theme-bg text-white px-3 py-1 rounded-full"
        >
          {{ getTravelStyleLabel(style) }}
          <button @click="toggleArrayFilter('selectedTravelStyles', style)" class="ml-2 text-white hover:text-theme-hover font-bold">×</button>
        </span>
        <span
          v-for="season in filters.selectedSeasons"
          :key="season"
          class="inline-flex items-center text-xs bg-theme-bg text-white px-3 py-1 rounded-full"
        >
          {{ getSeasonLabel(season) }}
          <button @click="toggleArrayFilter('selectedSeasons', season)" class="ml-2 text-white hover:text-theme-hover font-bold">×</button>
        </span>
        <span
          v-for="interest in filters.selectedSpecialInterests"
          :key="interest"
          class="inline-flex items-center text-xs bg-theme-bg text-white px-3 py-1 rounded-full"
        >
          {{ getSpecialInterestLabel(interest) }}
          <button @click="toggleArrayFilter('selectedSpecialInterests', interest)" class="ml-2 text-white hover:text-theme-hover font-bold">×</button>
        </span>
      </div>
    </div>

    <!-- Clear Filters Button -->
    <button
      @click="clearAllFilters"
      class="w-full px-6 py-3 text-sm font-semibold text-theme bg-theme-light border border-theme-border rounded-lg hover:bg-theme-bg transition-colors"
      :disabled="activeFiltersCount === 0"
      :class="{ 'opacity-50 cursor-not-allowed': activeFiltersCount === 0 }"
    >
      Alle filters wissen
    </button>
  </div>
</template>

<script>
export default {
  name: 'FilterSidebar',
  props: {
    filters: {
      type: Object,
      required: true
    },
    vacations: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      // Category mapping for intelligent categorization
      categoryMappings: {
        regions: ['europa', 'afrika', 'azië', 'noord-amerika', 'midden-amerika', 'zuid-amerika', 'oceanie', 'oceanië', 'caraiben', 'midden-oosten', 'middenoosten', 'antartica'],
        months: ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'],
        travelTypes: [
          'rondreis', 'cruise', 'cruises', 'all inclusive', 'verblijfsvakantie', 
          'vliegvakanties', 'autovakantie', 'safari', 'vakantie aan zee', 
          'vakantie met kinderen', 'groepsreis', 'busreis', 'citytrip', 
          'wandelreis', 'huwelijksreis', 'skivakantie', 'strandvakantie', 
          'weekendje weg', 'singlereizen', 'vakantiehuis'
        ],
        specialCategories: ['verras mij'],
        // Whitelist of actual countries per region (based on actual data)
        // ONLY actual countries, islands, and popular city destinations - NO brands, themes, travel types
        countries: {
          'europa': [
            // Countries
            'spanje', 'portugal', 'frankrijk', 'italië', 'griekenland', 'kroatië', 'kroatie',
            'turkije', 'cyprus', 'malta', 'ijsland', 'noorwegen', 'zweden', 'finland',
            'denemarken', 'duitsland', 'oostenrijk', 'slowakije', 'nederland', 'belgië',
            'belgië ', 'engeland', 'groot-brittannië', 'schotland', 'ierland', 'polen',
            'albanië', 'albanie', 'montenegro', 'estland', 'lapland',
            'bulgarije', 'hongarije', 'kosovo', 'letland', 'noord-macedonië', 'noord-macedonie',
            'roemenië', 'roemenie', 'slovenië', 'slovenie', 'tsjechië', 'tsjechie',
            // Islands & regions
            'sicilië', 'ibiza', 'tenerife', 'corfu', 'kreta', 'faeröer', 'canarische eilanden',
            // Cities
            'rome', 'porto', 'lissabon', 'dublin'
          ],
          'afrika': [
            // Countries
            'marokko', 'egypte', 'égypte', 'tunesië', 'kenia', 'tanzania',
            'oeganda', 'zuid-afrika', 'botswana', 'namibië', 'zimbabwe', 'madagaskar',
            'mauritius', 'seychellen', 'reunion', 'kaapverdië', 'senegal',
            'ethiopië', 'ethiopie', 'gambia', 'malawi', 'rwanda', 'zambia',
            // Islands & regions
            'zanzibar', 'sal', 'djerba'
          ],
          'azië': [
            // Countries
            'turkije', 'japan', 'china', 'india', 'sri lanka', 'thailand', 
            'vietnam', 'maleisië', 'indonesie', 'oman',
            'verenigde arabische emiraten', 'jordanië', 'zuid-korea',
            // Islands & regions
            'bali', 'malediven',
            // Cities
            'tokyo', 'dubai', 'rasalkhaimah'
          ],
          'noord-amerika': [
            // Countries
            'canada', 'verenigde staten', 'usa', 'mexico', 'honduras', 'costa rica', 'panama',
            // States & regions
            'florida', 'oregon', 'westkust',
            // Islands
            "bahama's",
            // Cities/Routes
            'lasvegas', 'route66'
          ],
          'zuid-amerika': [
            // Countries
            'colombia', 'peru', 'brazilië', 'chili', 'argentinië',
            'bolivië', 'bolivie', 'ecuador',
            // Regions
            'patagonië',
            // Cities
            'ushuaia', ' ushuaia'
          ],
          'midden-amerika': [
            // Countries
            'belize', 'cuba', 'guatemala', 'nicaragua', 'panama', 'suriname',
            'costa rica', 'mexico', 'honduras'
          ],
          'oceanie': [
            'australië', 'nieuw-zeeland'
          ],
          'oceanië': [
            'australië', 'nieuw-zeeland'
          ],
          'caraiben': [
            // Countries & Islands
            'cuba', 'jamaica', 'dominicaanse republiek', 'curaçao', 'curacao'
          ],
          'midden-oosten': [
            'turkije', 'dubai', 'verenigde arabische emiraten', 'oman', 'jordanië',
            'rasalkhaimah'
          ],
          'middenoosten': [
            'turkije', 'dubai', 'verenigde arabische emiraten', 'oman', 'jordanië',
            'rasalkhaimah'
          ],
          'antartica': [
            'argentinië', 'chili', 'ushuaia', ' ushuaia'
          ]
        }
      },
      
      // Labels for better display
      labelMappings: {
        // Regions
        'europa': 'Europa',
        'afrika': 'Afrika',
        'azië': 'Azië',
        'noord-amerika': 'Noord-Amerika',
        'midden-amerika': 'Midden-Amerika',
        'zuid-amerika': 'Zuid-Amerika',
        'oceanie': 'Oceanië',
        'oceanië': 'Oceanië',
        'caraiben': 'Caraïben',
        'midden-oosten': 'Midden-Oosten',
        'middenoosten': 'Midden-Oosten',
        'antartica': 'Antarctica',
        // Months
        'januari': 'Januari',
        'februari': 'Februari',
        'maart': 'Maart',
        'april': 'April',
        'mei': 'Mei',
        'juni': 'Juni',
        'juli': 'Juli',
        'augustus': 'Augustus',
        'september': 'September',
        'oktober': 'Oktober',
        'november': 'November',
        'december': 'December',
        // Travel types
        'rondreis': 'Rondreis',
        'cruise': 'Cruise',
        'cruises': 'Cruises',
        'all inclusive': 'All-inclusive',
        'verblijfsvakantie': 'Verblijfsvakantie',
        'vliegvakanties': 'Vliegvakanties',
        'autovakantie': 'Autovakantie',
        'safari': 'Safari',
        'vakantie aan zee': 'Vakantie aan zee',
        'vakantie met kinderen': 'Vakantie met kinderen',
        'groepsreis': 'Groepsreis',
        'busreis': 'Busreis',
        'citytrip': 'Citytrip',
        'wandelreis': 'Wandelreis',
        'huwelijksreis': 'Huwelijksreis',
        'skivakantie': 'Skivakantie',
        'strandvakantie': 'Strandvakantie',
        'weekendje weg': 'Weekendje weg',
        'singlereizen': 'Singlereizen',
        'vakantiehuis': 'Vakantiehuis',
        'verras mij': 'Verras mij',
        'vakanties': 'Vakanties',
        // Common countries (add more as needed)
        'spanje': 'Spanje',
        'egypte': 'Egypte',
        'égypte': 'Egypte',
        'kenia': 'Kenia',
        'dubai': 'Dubai',
        'marokko': 'Marokko',
        'tunesië': 'Tunesië',
        'kaapverdië': 'Kaapverdië',
        'senegal': 'Senegal',
        'tanzania': 'Tanzania',
        'namibië': 'Namibië',
        'oeganda': 'Oeganda',
        'madagaskar': 'Madagaskar',
        'reunion': 'Reunion',
        'sal': 'Sal',
        'zuid-afrika': 'Zuid-Afrika',
        'zanzibar': 'Zanzibar',
        'djerba': 'Djerba',
        'zimbabwe': 'Zimbabwe',
        'mauritius': 'Mauritius',
        'botswana': 'Botswana',
        'griekenland': 'Griekenland',
        'italië': 'Italië',
        'frankrijk': 'Frankrijk',
        'portugal': 'Portugal',
        'kroatië': 'Kroatië',
        'turkije': 'Turkije',
        'cyprus': 'Cyprus',
        'malta': 'Malta',
        'thailand': 'Thailand',
        'vietnam': 'Vietnam',
        'cambodja': 'Cambodja',
        'maleisië': 'Maleisië',
        'indonesië': 'Indonesië',
        'bali': 'Bali',
        'sri lanka': 'Sri Lanka',
        'maldiven': 'Maldiven',
        'india': 'India',
        'china': 'China',
        'japan': 'Japan',
        'verenigde arabische emiraten': 'Verenigde Arabische Emiraten',
        'mexico': 'Mexico',
        'costa rica': 'Costa Rica',
        'cuba': 'Cuba',
        'dominicaanse republiek': 'Dominicaanse Republiek',
        'brazilië': 'Brazilië',
        'peru': 'Peru',
        'chili': 'Chili',
        'argentinië': 'Argentinië',
        'canada': 'Canada',
        'verenigde staten': 'Verenigde Staten',
        'usa': 'USA',
        'amerika': 'Amerika',
        'australië': 'Australië',
        'nieuw-zeeland': 'Nieuw-Zeeland',
        'curaçao': 'Curaçao',
        'curacao': 'Curaçao',
        'aruba': 'Aruba',
        'bonaire': 'Bonaire',
        'jamaica': 'Jamaica',
        'seychellen': 'Seychellen',
        'florida': 'Florida',
        'oregon': 'Oregon',
        'westkust': 'Westkust',
        'lasvegas': 'Las Vegas',
        'route66': 'Route 66',
        'patagonië': 'Patagonië',
        'ushuaia': 'Ushuaia',
        ' ushuaia': 'Ushuaia',
        'zuidpool': 'Zuidpool',
        'tokyo': 'Tokyo',
        'zuid-korea': 'Zuid-Korea',
        'rasalkhaimah': 'Ras Al Khaimah',
        'sicilië': 'Sicilië',
        'groot-brittannië': 'Groot-Brittannië',
        'schotland': 'Schotland',
        'faeröer': 'Faeröer',
        'corfu': 'Corfu',
        'kreta': 'Kreta',
        'ibiza': 'Ibiza',
        'tenerife': 'Tenerife',
        'rome': 'Rome',
        'dublin': 'Dublin',
        'porto': 'Porto',
        'lissabon': 'Lissabon',
        'colombië': 'Colombia',
        "bahama's": "Bahama's",
        'honduras': 'Honduras',
        'panama': 'Panama',
        'indonesie': 'Indonesië',
        'lapland': 'Lapland',
        'canarische eilanden': 'Canarische Eilanden',
        // New Afrika countries
        'ethiopië': 'Ethiopië',
        'ethiopie': 'Ethiopië',
        'gambia': 'Gambia',
        'malawi': 'Malawi',
        'rwanda': 'Rwanda',
        'zambia': 'Zambia',
        // New Europa countries
        'albanie': 'Albanië',
        'bulgarije': 'Bulgarije',
        'hongarije': 'Hongarije',
        'kosovo': 'Kosovo',
        'kroatie': 'Kroatië',
        'letland': 'Letland',
        'noord-macedonië': 'Noord-Macedonië',
        'noord-macedonie': 'Noord-Macedonië',
        'roemenië': 'Roemenië',
        'roemenie': 'Roemenië',
        'slovenië': 'Slovenië',
        'slovenie': 'Slovenië',
        'tsjechië': 'Tsjechië',
        'tsjechie': 'Tsjechië',
        // New Midden-Amerika countries
        'belize': 'Belize',
        'guatemala': 'Guatemala',
        'nicaragua': 'Nicaragua',
        'suriname': 'Suriname',
        // New Zuid-Amerika countries
        'bolivië': 'Bolivië',
        'bolivie': 'Bolivië',
        'ecuador': 'Ecuador',
        // Oceanië
        'nieuw-zeeland': 'Nieuw-Zeeland'
      }
    }
  },
  
  computed: {
    // Extract all unique categories from vacation data
    allCategories() {
      const categories = new Set()
      this.vacations.forEach(vacation => {
        if (vacation.metadata && vacation.metadata.categories) {
          vacation.metadata.categories.forEach(category => {
            categories.add(category.toLowerCase())
          })
        }
      })
      return Array.from(categories)
    },

    // Dynamically generate regions from actual data
    regions() {
      const availableRegions = this.allCategories.filter(cat => 
        this.categoryMappings.regions.includes(cat)
      )
      return availableRegions.map(region => ({
        value: region,
        label: this.labelMappings[region] || this.formatLabel(region)
      }))
    },

    // Dynamically generate countries based on selected region
    countries() {
      if (!this.filters.selectedRegion) return []
      
      // Get the whitelist of valid countries for the selected region
      const validCountries = this.categoryMappings.countries[this.filters.selectedRegion] || []
      
      // Find which of these countries actually exist in the vacation data
      // Note: Countries may not always be tagged with their region, so we check
      // all vacations for countries in the whitelist
      const availableCountries = new Set()
      this.vacations.forEach(vacation => {
        if (vacation.metadata && vacation.metadata.categories) {
          const categories = vacation.metadata.categories.map(cat => cat.toLowerCase())
          categories.forEach(cat => {
            // Include if it's in the whitelist for this region
            if (validCountries.includes(cat)) {
              availableCountries.add(cat)
            }
          })
        }
      })

      // Sort alphabetically by label
      return Array.from(availableCountries)
        .sort((a, b) => {
          const labelA = this.labelMappings[a] || this.formatLabel(a)
          const labelB = this.labelMappings[b] || this.formatLabel(b)
          return labelA.localeCompare(labelB)
        })
        .map(country => ({
          value: country,
          label: this.labelMappings[country] || this.formatLabel(country)
        }))
    },

    // Dynamically generate travel styles from actual data (sorted alphabetically by label)
    travelStyles() {
      const availableStyles = this.allCategories.filter(cat => 
        this.categoryMappings.travelTypes.includes(cat)
      )
      return availableStyles
        .map(style => ({
          value: style,
          label: this.labelMappings[style] || this.formatLabel(style)
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'nl'))
    },

    // Dynamically generate months from actual data in chronological order
    seasons() {
      const availableMonths = this.allCategories.filter(cat => 
        this.categoryMappings.months.includes(cat)
      )
      
      // Sort months in chronological order
      const monthOrder = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december']
      const sortedMonths = availableMonths.sort((a, b) => {
        return monthOrder.indexOf(a) - monthOrder.indexOf(b)
      })
      
      return sortedMonths.map(month => ({
        value: month,
        label: this.labelMappings[month] || this.formatLabel(month)
      }))
    },

    // Special interests from remaining categories
    specialInterests() {
      const specialCategories = this.allCategories.filter(cat => 
        this.categoryMappings.specialCategories.includes(cat)
      )
      return specialCategories.map(category => ({
        value: category,
        label: this.labelMappings[category] || this.formatLabel(category)
      }))
    },

    filteredCountries() {
      return this.countries
    },
    
    activeFiltersCount() {
      let count = 0
      if (this.filters.searchQuery) count++
      if (this.filters.selectedRegion) count++
      if (this.filters.selectedCountry) count++
      count += this.filters.selectedTravelStyles.length
      count += this.filters.selectedSeasons.length
      count += this.filters.selectedSpecialInterests.length
      return count
    }
  },
  
  methods: {
    updateFilter(filterName, value) {
      this.$emit('filter-change', { [filterName]: value })
    },
    
    toggleArrayFilter(filterName, value) {
      const currentArray = [...this.filters[filterName]]
      const index = currentArray.indexOf(value)
      
      if (index > -1) {
        currentArray.splice(index, 1)
      } else {
        currentArray.push(value)
      }
      
      this.updateFilter(filterName, currentArray)
    },
    
    onRegionChange(region) {
      this.$emit('filter-change', {
        selectedRegion: region,
        selectedCountry: '' // Reset country when region changes
      })
    },
    
    clearAllFilters() {
      this.$emit('clear-filters')
    },

    formatLabel(value) {
      // Convert kebab-case or snake_case to Title Case
      return value
        .split(/[-_\s]+/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
    
    getRegionLabel(value) {
      const region = this.regions.find(r => r.value === value)
      return region ? region.label : this.formatLabel(value)
    },
    
    getCountryLabel(value) {
      const country = this.countries.find(c => c.value === value)
      return country ? country.label : this.formatLabel(value)
    },
    
    getTravelStyleLabel(value) {
      const style = this.travelStyles.find(s => s.value === value)
      return style ? style.label : this.formatLabel(value)
    },
    
    getSeasonLabel(value) {
      const season = this.seasons.find(s => s.value === value)
      return season ? season.label : this.formatLabel(value)
    },
    
    getSpecialInterestLabel(value) {
      const interest = this.specialInterests.find(i => i.value === value)
      return interest ? interest.label : this.formatLabel(value)
    }
  }
}
</script>

<style>
.text-theme {
  color: var(--theme);
}

.text-theme-hover {
  color: var(--theme-hover);
}

.bg-theme-light {
  background-color: var(--theme-light);
}

.bg-theme-bg {
  background-color: var(--theme);
}

.border-theme-border {
  border-color: var(--theme-border);
}

.focus\:ring-theme:focus {
  --tw-ring-color: var(--theme);
}
select {
  background: unset !important;
}
</style> 